import {
  FilterExpression,
  ComparisonCondition,
  CompoundCondition,
  NullCondition,
  InCondition,
  BetweenCondition,
  NotCondition,
  ComparisonOperator,
  DataValue,
  ParseResult,
  createColumnName
} from '../../types';

/**
 * 安全なフィルタ式言語のパーサーと評価器
 *
 * クオート仕様（DuckDB SQL標準に準拠）:
 * - 文字列リテラル: シングルクオート 'value'
 * - 列名（識別子）: ダブルクオート "列名"（特殊文字を含む場合）
 * - エスケープ: クオート2つ重ね '' または ""
 *
 * サポートする構文:
 * - 比較演算: column = value, column != value, column > value など
 * - パターンマッチ: column LIKE '%text%', column ILIKE '%text%'
 * - NULL判定: column IS NULL, column IS NOT NULL
 * - 複数値マッチ: column IN ('a', 'b', 'c'), column NOT IN (1, 2, 3)
 * - 範囲指定: column BETWEEN 10 AND 20, column NOT BETWEEN 10 AND 20
 * - 条件否定: NOT (condition)
 * - 論理演算: condition1 AND condition2, condition1 OR condition2
 * - 括弧によるグループ化: (condition1 OR condition2) AND condition3
 *
 * 例:
 * - age > 25
 * - name LIKE '%田%'
 * - age >= 20 AND department = 'IT'
 * - (age > 30 OR salary > 50000) AND active = true
 * - "名前" = '太郎'
 * - email IS NOT NULL
 * - status IN ('active', 'pending')
 * - age BETWEEN 20 AND 30
 * - NOT (status = 'deleted')
 */

/**
 * トークンの種類
 */
type TokenType = 'identifier' | 'string' | 'number' | 'keyword' | 'operator' | 'paren' | 'comma';

/**
 * トークン
 */
interface Token {
  type: TokenType;
  value: string;
}

export class FilterExpressionParser {
  private tokens: Token[] = [];
  private position = 0;

  /**
   * 文字列式をパースしてFilterExpressionに変換
   * @param expression パースする式の文字列
   * @returns パース結果
   */
  parse(expression: string): ParseResult {
    try {
      this.tokens = this.tokenize(expression);
      this.position = 0;

      if (this.tokens.length === 0) {
        return { success: false, error: 'Expression is empty' };
      }

      const result = this.parseExpression();

      if (this.position < this.tokens.length) {
        return { success: false, error: `Unexpected token: ${this.tokens[this.position].value}` };
      }

      return { success: true, expression: result };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * 式を評価してデータ行がフィルタ条件に合致するかを判定
   * @param expression フィルタ式
   * @param rowData 行のデータ
   * @returns 条件に合致するかどうか
   */
  static evaluate(expression: FilterExpression, rowData: Record<string, DataValue>): boolean {
    // 条件の種類を判定
    if ('type' in expression) {
      switch (expression.type) {
        case 'compound':
          return FilterExpressionParser.evaluateCompound(expression, rowData);
        case 'null_check':
          return FilterExpressionParser.evaluateNullCheck(expression, rowData);
        case 'in':
          return FilterExpressionParser.evaluateIn(expression, rowData);
        case 'between':
          return FilterExpressionParser.evaluateBetween(expression, rowData);
        case 'not':
          return !FilterExpressionParser.evaluate(expression.condition, rowData);
      }
    }
    // ComparisonCondition（typeプロパティなし）
    return FilterExpressionParser.evaluateComparison(expression as ComparisonCondition, rowData);
  }

  /**
   * 複合条件の評価
   */
  private static evaluateCompound(condition: CompoundCondition, rowData: Record<string, DataValue>): boolean {
    if (condition.conditions.length === 0) return true;

    let result = FilterExpressionParser.evaluate(condition.conditions[0], rowData);

    for (let i = 1; i < condition.conditions.length; i++) {
      const conditionResult = FilterExpressionParser.evaluate(condition.conditions[i], rowData);

      if (condition.operator === 'and') {
        result = result && conditionResult;
      } else {
        result = result || conditionResult;
      }
    }

    return result;
  }

  /**
   * 比較条件の評価
   */
  private static evaluateComparison(condition: ComparisonCondition, rowData: Record<string, DataValue>): boolean {
    const columnValue = rowData[condition.column];
    const filterValue = condition.value;

    // null値の処理
    if (columnValue === null || columnValue === undefined) {
      return filterValue === null || filterValue === undefined;
    }

    switch (condition.operator) {
      case '=':
        return columnValue === filterValue;
      case '!=':
        return columnValue !== filterValue;
      case '>':
        return typeof columnValue === 'number' && typeof filterValue === 'number' && columnValue > filterValue;
      case '<':
        return typeof columnValue === 'number' && typeof filterValue === 'number' && columnValue < filterValue;
      case '>=':
        return typeof columnValue === 'number' && typeof filterValue === 'number' && columnValue >= filterValue;
      case '<=':
        return typeof columnValue === 'number' && typeof filterValue === 'number' && columnValue <= filterValue;
      case 'like':
        return FilterExpressionParser.matchLikePattern(columnValue, filterValue, false);
      case 'ilike':
        return FilterExpressionParser.matchLikePattern(columnValue, filterValue, true);
      default:
        return false;
    }
  }

  /**
   * LIKEパターンマッチング
   * % は任意の文字列（0文字以上）
   * _ は任意の1文字
   */
  private static matchLikePattern(value: DataValue, pattern: DataValue, caseInsensitive: boolean): boolean {
    if (typeof value !== 'string' || typeof pattern !== 'string') {
      return false;
    }

    let strValue = value;
    let strPattern = pattern;

    if (caseInsensitive) {
      strValue = strValue.toLowerCase();
      strPattern = strPattern.toLowerCase();
    }

    // パターンを正規表現に変換
    // 特殊文字をエスケープし、% と _ を正規表現に変換
    const regexPattern = strPattern
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // 正規表現の特殊文字をエスケープ
      .replace(/%/g, '.*')  // % → .*
      .replace(/_/g, '.');  // _ → .

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(strValue);
  }

  /**
   * NULL判定の評価
   */
  private static evaluateNullCheck(condition: NullCondition, rowData: Record<string, DataValue>): boolean {
    const columnValue = rowData[condition.column];
    const isNull = columnValue === null || columnValue === undefined;
    return condition.isNull ? isNull : !isNull;
  }

  /**
   * IN条件の評価
   */
  private static evaluateIn(condition: InCondition, rowData: Record<string, DataValue>): boolean {
    const columnValue = rowData[condition.column];
    const isIncluded = condition.values.some(v => v === columnValue);
    return condition.negated ? !isIncluded : isIncluded;
  }

  /**
   * BETWEEN条件の評価
   */
  private static evaluateBetween(condition: BetweenCondition, rowData: Record<string, DataValue>): boolean {
    const columnValue = rowData[condition.column];

    if (typeof columnValue !== 'number' ||
        typeof condition.lower !== 'number' ||
        typeof condition.upper !== 'number') {
      return false;
    }

    const inRange = columnValue >= condition.lower && columnValue <= condition.upper;
    return condition.negated ? !inRange : inRange;
  }

  /**
   * 式をトークンに分割
   */
  private tokenize(expression: string): Token[] {
    const tokens: Token[] = [];
    let i = 0;

    // キーワード一覧
    const keywords = ['and', 'or', 'null', 'true', 'false', 'is', 'not', 'in', 'between', 'like', 'ilike'];

    while (i < expression.length) {
      const char = expression[i];

      // 空白をスキップ
      if (/\s/.test(char)) {
        i++;
        continue;
      }

      // ダブルクオート: 識別子（列名）
      if (char === '"') {
        i++; // 開始クォートをスキップ
        let identifier = '';

        while (i < expression.length) {
          if (expression[i] === '"') {
            if (i + 1 < expression.length && expression[i + 1] === '"') {
              // "" はエスケープされたダブルクオート
              identifier += '"';
              i += 2;
            } else {
              // 識別子の終了
              break;
            }
          } else {
            identifier += expression[i];
            i++;
          }
        }

        if (i >= expression.length) {
          throw new Error('Unclosed identifier');
        }

        i++; // 終了クォートをスキップ
        tokens.push({ type: 'identifier', value: identifier });
        continue;
      }

      // シングルクオート: 文字列リテラル
      if (char === "'") {
        i++; // 開始クォートをスキップ
        let str = '';

        while (i < expression.length) {
          if (expression[i] === "'") {
            if (i + 1 < expression.length && expression[i + 1] === "'") {
              // '' はエスケープされたシングルクオート
              str += "'";
              i += 2;
            } else {
              // 文字列の終了
              break;
            }
          } else {
            str += expression[i];
            i++;
          }
        }

        if (i >= expression.length) {
          throw new Error('Unclosed string literal');
        }

        i++; // 終了クォートをスキップ
        tokens.push({ type: 'string', value: str });
        continue;
      }

      // 2文字演算子
      if (i + 1 < expression.length) {
        const twoChar = expression.substring(i, i + 2);
        if (['>=', '<=', '!='].includes(twoChar)) {
          tokens.push({ type: 'operator', value: twoChar });
          i += 2;
          continue;
        }
      }

      // 1文字演算子
      if (['>', '<', '='].includes(char)) {
        tokens.push({ type: 'operator', value: char });
        i++;
        continue;
      }

      // 括弧
      if (char === '(' || char === ')') {
        tokens.push({ type: 'paren', value: char });
        i++;
        continue;
      }

      // カンマ
      if (char === ',') {
        tokens.push({ type: 'comma', value: ',' });
        i++;
        continue;
      }

      // 識別子、数値、キーワード
      let token = '';
      // 日本語文字も識別子として許可（Unicode対応）
      while (i < expression.length && /[a-zA-Z0-9_.@\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(expression[i])) {
        token += expression[i];
        i++;
      }

      if (token) {
        const lowerToken = token.toLowerCase();

        // キーワード
        if (keywords.includes(lowerToken)) {
          tokens.push({ type: 'keyword', value: lowerToken });
          continue;
        }

        // 数値
        if (/^-?\d+(\.\d+)?$/.test(token)) {
          tokens.push({ type: 'number', value: token });
          continue;
        }

        // 識別子（列名）
        tokens.push({ type: 'identifier', value: token });
        continue;
      }

      throw new Error(`Unexpected character: ${char}`);
    }

    return tokens;
  }

  /**
   * 現在のトークンを取得
   */
  private currentToken(): Token | null {
    return this.position < this.tokens.length ? this.tokens[this.position] : null;
  }

  /**
   * 次のトークンに進む
   */
  private advance(): void {
    this.position++;
  }

  /**
   * 式全体をパース
   */
  private parseExpression(): FilterExpression {
    return this.parseOrExpression();
  }

  /**
   * OR式をパース
   */
  private parseOrExpression(): FilterExpression {
    let left = this.parseAndExpression();

    while (this.currentToken()?.type === 'keyword' && this.currentToken()?.value === 'or') {
      this.advance(); // 'or'をスキップ
      const right = this.parseAndExpression();

      if ('type' in left && left.type === 'compound' && left.operator === 'or') {
        // 既存のOR条件に追加
        left.conditions.push(right);
      } else {
        // 新しいOR条件を作成
        left = {
          type: 'compound',
          conditions: [left, right],
          operator: 'or'
        };
      }
    }

    return left;
  }

  /**
   * AND式をパース
   */
  private parseAndExpression(): FilterExpression {
    let left = this.parsePrimaryExpression();

    while (this.currentToken()?.type === 'keyword' && this.currentToken()?.value === 'and') {
      this.advance(); // 'and'をスキップ
      const right = this.parsePrimaryExpression();

      if ('type' in left && left.type === 'compound' && left.operator === 'and') {
        // 既存のAND条件に追加
        left.conditions.push(right);
      } else {
        // 新しいAND条件を作成
        left = {
          type: 'compound',
          conditions: [left, right],
          operator: 'and'
        };
      }
    }

    return left;
  }

  /**
   * 基本式をパース（比較式、NOT式、または括弧で囲まれた式）
   */
  private parsePrimaryExpression(): FilterExpression {
    const token = this.currentToken();

    // NOT条件
    if (token?.type === 'keyword' && token.value === 'not') {
      this.advance(); // 'not'をスキップ
      const condition = this.parsePrimaryExpression();
      return {
        type: 'not',
        condition
      } as NotCondition;
    }

    // 括弧で囲まれた式
    if (token?.type === 'paren' && token.value === '(') {
      this.advance(); // '('をスキップ
      const expr = this.parseExpression();

      const closeToken = this.currentToken();
      if (closeToken?.type !== 'paren' || closeToken.value !== ')') {
        throw new Error('Missing closing parenthesis');
      }

      this.advance(); // ')'をスキップ
      return expr;
    }

    return this.parseCondition();
  }

  /**
   * 条件式をパース（比較、IS NULL、IN、BETWEEN など）
   */
  private parseCondition(): FilterExpression {
    const columnToken = this.currentToken();
    if (!columnToken || columnToken.type !== 'identifier') {
      throw new Error('Column name is required');
    }
    const column = columnToken.value;
    this.advance();

    // NOT IN, NOT BETWEEN, NOT LIKE の処理
    let negated = false;
    if (this.currentToken()?.type === 'keyword' && this.currentToken()?.value === 'not') {
      negated = true;
      this.advance();
    }

    const operatorToken = this.currentToken();
    if (!operatorToken) {
      throw new Error('Operator is required');
    }

    // IS NULL / IS NOT NULL
    if (operatorToken.type === 'keyword' && operatorToken.value === 'is') {
      this.advance(); // 'is'をスキップ

      let isNull = true;
      if (this.currentToken()?.type === 'keyword' && this.currentToken()?.value === 'not') {
        isNull = false;
        this.advance(); // 'not'をスキップ
      }

      if (this.currentToken()?.type !== 'keyword' || this.currentToken()?.value !== 'null') {
        throw new Error('Expected NULL after IS / IS NOT');
      }
      this.advance(); // 'null'をスキップ

      return {
        type: 'null_check',
        column: createColumnName(column),
        isNull
      } as NullCondition;
    }

    // IN (...)
    if (operatorToken.type === 'keyword' && operatorToken.value === 'in') {
      this.advance(); // 'in'をスキップ
      const values = this.parseValueList();
      return {
        type: 'in',
        column: createColumnName(column),
        values,
        negated
      } as InCondition;
    }

    // BETWEEN ... AND ...
    if (operatorToken.type === 'keyword' && operatorToken.value === 'between') {
      this.advance(); // 'between'をスキップ
      const lower = this.parseValue();

      if (this.currentToken()?.type !== 'keyword' || this.currentToken()?.value !== 'and') {
        throw new Error('Expected AND in BETWEEN expression');
      }
      this.advance(); // 'and'をスキップ

      const upper = this.parseValue();

      return {
        type: 'between',
        column: createColumnName(column),
        lower,
        upper,
        negated
      } as BetweenCondition;
    }

    // LIKE / ILIKE
    if (operatorToken.type === 'keyword' && (operatorToken.value === 'like' || operatorToken.value === 'ilike')) {
      const operator = operatorToken.value as 'like' | 'ilike';
      this.advance(); // 'like' / 'ilike'をスキップ
      const value = this.parseValue();

      const condition: ComparisonCondition = {
        column: createColumnName(column),
        operator,
        value
      };

      // NOT LIKE / NOT ILIKE
      if (negated) {
        return {
          type: 'not',
          condition
        } as NotCondition;
      }

      return condition;
    }

    // negatedがtrueの場合、ここに到達するのは不正
    if (negated) {
      throw new Error('NOT can only be used with IN, BETWEEN, LIKE, or ILIKE');
    }

    // 通常の比較演算子
    if (operatorToken.type !== 'operator') {
      throw new Error(`Unknown operator: ${operatorToken.value}`);
    }

    let operator: ComparisonOperator;

    switch (operatorToken.value) {
      case '=':
      case '!=':
      case '>':
      case '<':
      case '>=':
      case '<=':
        operator = operatorToken.value;
        this.advance();
        break;
      default:
        throw new Error(`Unknown operator: ${operatorToken.value}`);
    }

    const value = this.parseValue();

    return {
      column: createColumnName(column),
      operator,
      value
    };
  }

  /**
   * 値リスト (a, b, c) をパース
   */
  private parseValueList(): DataValue[] {
    if (this.currentToken()?.type !== 'paren' || this.currentToken()?.value !== '(') {
      throw new Error('Expected ( for value list');
    }
    this.advance(); // '('をスキップ

    const values: DataValue[] = [];

    // 最初の値
    values.push(this.parseValue());

    // 残りの値
    while (this.currentToken()?.type === 'comma') {
      this.advance(); // ','をスキップ
      values.push(this.parseValue());
    }

    if (this.currentToken()?.type !== 'paren' || this.currentToken()?.value !== ')') {
      throw new Error('Expected ) for value list');
    }
    this.advance(); // ')'をスキップ

    return values;
  }

  /**
   * 値をパース
   */
  private parseValue(): DataValue {
    const valueToken = this.currentToken();
    if (valueToken === null) {
      throw new Error('Value is required');
    }
    this.advance();

    switch (valueToken.type) {
      case 'keyword':
        if (valueToken.value === 'null') {
          return null;
        } else if (valueToken.value === 'true') {
          return true;
        } else if (valueToken.value === 'false') {
          return false;
        } else {
          throw new Error(`Unexpected keyword: ${valueToken.value}`);
        }
      case 'number':
        if (/^-?\d+$/.test(valueToken.value)) {
          return parseInt(valueToken.value, 10);
        } else {
          return parseFloat(valueToken.value);
        }
      case 'string':
        return valueToken.value;
      default:
        throw new Error(`Unexpected token type for value: ${valueToken.type}`);
    }
  }
}
